import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, List, Share2, Clock, User, Tag } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import BlogLeadPopup from '../components/BlogLeadPopup';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { enhanceHtmlContent } from '../utils/htmlUtils';
import { initializeFAQAccordions, applyFAQStyles } from '../utils/faqUtils';
import { useScrollToTop } from '../hooks/useScrollToTop';
import AdminEditButton from '../components/AdminEditButton';
import { useLastParagraphDetection } from '../hooks/useLastParagraphDetection';
import BlogInlineLeadForm from '../components/BlogInlineLeadForm';

// Custom Link component that scrolls to top
const ScrollToTopLink = ({ to, children, className, ...props }: { to: string; children: React.ReactNode; className?: string; [key: string]: any }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Scroll to top first
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Then navigate after a short delay to ensure scroll completes
    setTimeout(() => {
      navigate(to);
    }, 100);
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

// Helper function to extract headings from HTML content
const extractHeadings = (htmlContent: string): { id: string; text: string; level: number }[] => {
  if (!htmlContent) return [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  const headings = Array.from(tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  return headings
    .map((heading) => {
      const text = heading.textContent || '';
      const level = parseInt(heading.tagName.substring(1), 10);
      const id = heading.id;
      if (!id) return null;
      return { id, text, level };
    })
    .filter(Boolean) as { id: string; text: string; level: number }[];
};

// Helper function to handle scrolling to a hash link
const handleHashChange = (hash: string) => {
  if (hash) {
    try {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (e) {
      console.error('Error scrolling to hash:', e);
    }
  }
};

// Table of Contents Component
const TableOfContents = ({ headings }: { headings: { id: string; text: string; level: number }[] }) => {
  const handleTocClick = (e: React.MouseEvent, hash: string) => {
    e.preventDefault();
    handleHashChange(hash);
    // Optional: update URL hash without triggering a full navigation
    window.history.pushState(null, '', hash);
  };

  return (
    <div className="bg-gray-100 p-4 rounded-lg">
      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
        <List size={20} className="mr-2" />
        Table of Contents
      </h3>
      <ul className="space-y-2">
        {headings.map(({ id, text, level }) => (
          <li key={id} style={{ marginLeft: `${(level - 1) * 1}rem` }}>
            <a
              href={`#${id}`}
              onClick={(e) => handleTocClick(e, `#${id}`)}
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 block"
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { blogPosts, loading } = useData();
  const [post, setPost] = useState<any>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [enhancedContent, setEnhancedContent] = useState<string>('');
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);
  const [showToc, setShowToc] = useState<boolean>(false);
  const [showLeadPopup, setShowLeadPopup] = useState<boolean>(false);
  const [hasShownPopup, setHasShownPopup] = useState<boolean>(false);

  // Simple test to see if the component is rendering

  useEffect(() => {
    if (loading) {
      return;
    }
    
    if (slug && blogPosts.length > 0) {
      const foundPost = blogPosts.find(p => p.slug === slug && p.status === 'published');
      
      if (foundPost) {
        setPost(foundPost);

        const enhanced = enhanceHtmlContent(foundPost.content);
        setEnhancedContent(enhanced);

        try {
          const extractedHeadings = extractHeadings(enhanced);
          setHeadings(extractedHeadings);
          setShowToc(extractedHeadings.length >= 3);
        } catch (error) {
          console.error('ERROR: Failed to extract headings.', error);
          setHeadings([]);
          setShowToc(false);
        }

        const related = blogPosts
          .filter(p => 
            p.id !== foundPost.id && 
            p.status === 'published'
          )
          .map(post => {
            let score = 0;
            if (foundPost.categories && post.categories) {
              const categoryMatches = foundPost.categories.filter(cat => 
                post.categories.includes(cat)
              ).length;
              score += categoryMatches * 10;
            }
            if (foundPost.tags && post.tags) {
              const tagMatches = foundPost.tags.filter(tag => 
                post.tags.includes(tag)
              ).length;
              score += tagMatches * 5;
            }
            const daysSincePublished = (Date.now() - new Date(post.publishedAt || post.createdAt).getTime()) / (1000 * 60 * 60 * 24);
            if (daysSincePublished < 30) score += 2;
            else if (daysSincePublished < 90) score += 1;
            return { ...post, score };
          })
          .sort((a, b) => b.score - a.score)
          .slice(0, 8);
        
        if (related.length < 5) {
          const recentPosts = blogPosts
            .filter(p => 
              p.id !== foundPost.id && 
              p.status === 'published' &&
              !related.find(rp => rp.id === p.id)
            )
            .sort((a, b) => new Date(b.publishedAt || b.createdAt).getTime() - new Date(a.publishedAt || a.createdAt).getTime())
            .slice(0, 8 - related.length);
          
          setRelatedPosts([...related, ...recentPosts]);
        } else {
          setRelatedPosts(related);
        }

      } else {
        setPost(null); // Explicitly set post to null if not found
      }
    } else if (slug) {
    }
  }, [slug, blogPosts, loading]);

  // Scroll to top when component mounts or slug changes
  useScrollToTop();

  // Handle URL hash changes for direct navigation to sections
  useEffect(() => {
    if (post && window.location.hash) {
      handleHashChange(window.location.hash);
    }
  }, [post]);

  // Handle last paragraph detection for lead popup
  const handleLastParagraphReached = () => {
    if (!hasShownPopup && post) {
      setShowLeadPopup(true);
      setHasShownPopup(true);
    }
  };

  useLastParagraphDetection({
    content: enhancedContent,
    onLastParagraphReached: handleLastParagraphReached,
    threshold: 0.3
  });

  const handleCloseLeadPopup = () => {
    setShowLeadPopup(false);
  };

  // Update document title and meta description
  useEffect(() => {
    if (post) {
      document.title = post.seoTitle || post.title;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && post.seoDescription) {
        metaDescription.setAttribute('content', post.seoDescription);
      }
    }
  }, [post]);

  // Initialize FAQ/Accordion functionality for imported content
  useEffect(() => {
    // Apply FAQ styles once
    applyFAQStyles();

    // Initialize FAQ accordions when content is rendered
    const contentContainer = document.querySelector('[class*="prose"]');
    if (contentContainer) {
      // Give the DOM a moment to render
      setTimeout(() => {
        initializeFAQAccordions(contentContainer as HTMLElement);
      }, 100);
    }
  }, [enhancedContent]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || post.seoDescription,
          url: window.location.href,
        });
      } catch (error) {
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 text-lg">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-4">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-medium mt-4"
          >
            <ArrowLeft size={16} />
            <span>Back to Blog</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Admin Edit Button - Only visible to logged-in admins */}
      {post && <AdminEditButton type="blog" id={post.id} />}
      
      {/* Article Header */}
      <article className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-medium"
            >
              <ArrowLeft size={16} />
              <span>Back to Home</span>
            </Link>
          </nav>

          {/* Article Header */}
          <header className="max-w-4xl mx-auto mb-12">
            {/* Categories */}
            {post.categories && post.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.categories.map((category: string, index: number) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800"
                  >
                    <Tag size={14} className="mr-1" />
                    {category}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-8">
              <div className="flex items-center space-x-6 text-gray-600">
                <div className="flex items-center space-x-2">
                  <Clock size={16} />
                  <span>
                    {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>Simran Industries</span>
                </div>
              </div>
              
              <button
                onClick={handleShare}
                className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-medium"
              >
                <Share2 size={16} />
                <span>Share</span>
              </button>
            </div>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-gray-700 leading-relaxed mb-8 font-medium">
                {post.excerpt}
              </p>
            )}

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="mb-12">
                <img 
                  src={post.featuredImage} 
                  alt={post.title}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  className="w-full h-96 rounded-2xl shadow-2xl object-cover"
                />
              </div>
            )}
          </header>

          <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
            {/* Table of Contents - Desktop */}
            {showToc && (
              <aside className="lg:w-1/4 hidden lg:block">
                <div className="sticky top-24">
                  <TableOfContents headings={headings} />
                </div>
              </aside>
            )}

            {/* Article Content */}
            <div className={`${showToc ? 'lg:w-3/4' : 'w-full'} max-w-4xl mx-auto`}>
              {/* Mobile TOC Toggle */}
              {showToc && (
                <div className="lg:hidden mb-6">
                  <button
                    onClick={() => setShowToc(!showToc)}
                    className="flex items-center space-x-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-lg w-full"
                  >
                    <List size={20} />
                    <span>Table of Contents</span>
                  </button>
                  
                  {showToc && (
                    <div className="mt-4">
                      <TableOfContents headings={headings} />
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 mb-12">
                <div 
                  className="prose prose-lg max-w-none prose-headings:text-amber-800 prose-headings:scroll-mt-24 prose-links:text-amber-600 prose-links:hover:text-amber-700 prose-strong:text-gray-900 prose-blockquote:border-amber-500 prose-blockquote:bg-amber-50 prose-blockquote:text-amber-900"
                  dangerouslySetInnerHTML={{ __html: enhancedContent }}
                />
              </div>

              {/* Inline Contact Form - shown before tags */}
              {post?.title && (
                <div className="mb-12">
                  <BlogInlineLeadForm title={post.title} />
                </div>
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag: string, index: number) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Bio */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-8 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">S</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-amber-800 mb-2">Simran Industries</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Leading manufacturer of premium cotton seed cake and cattle feed in India. 
                      With over 25 years of experience, we're committed to providing quality 
                      nutrition solutions for dairy farmers and livestock owners across the nation.
                    </p>
                    <div className="mt-4">
                      <Link
                        to="/#contact"
                        className="text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Contact Us →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-amber-800 mb-12 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {relatedPosts.map((relatedPost) => (
                <article key={relatedPost.id} className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group">
                  {relatedPost.featuredImage && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={relatedPost.featuredImage} 
                        alt={relatedPost.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full group-hover:scale-110 transition-transform duration-300 object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {/* Categories */}
                    {relatedPost.categories && relatedPost.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {relatedPost.categories.slice(0, 2).map((category: string, index: number) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                      <ScrollToTopLink 
                        to={`/${relatedPost.slug}`}
                        className="hover:text-amber-600 transition-colors"
                      >
                        {relatedPost.title}
                      </ScrollToTopLink>
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
                      {relatedPost.excerpt}
                    </p>
                    
                    {/* Date */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {new Date(relatedPost.publishedAt || relatedPost.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <ScrollToTopLink
                        to={`/${relatedPost.slug}`}
                        className="text-amber-600 hover:text-amber-700 font-medium text-sm"
                      >
                        Read More →
                      </ScrollToTopLink>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            
            {/* View All Posts Link */}
            <div className="text-center mt-12">
              <ScrollToTopLink
                to="/blog"
                className="inline-flex items-center space-x-2 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors font-medium"
              >
                <span>View All Blog Posts</span>
                <ArrowLeft size={16} className="rotate-180" />
              </ScrollToTopLink>
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Blog Lead Popup */}
      {showLeadPopup && post && (
        <BlogLeadPopup
          isOpen={showLeadPopup}
          onClose={handleCloseLeadPopup}
          productName={post.title}
          image={post.featuredImage}
          title={post.title}
        />
      )}
    </div>
  );
};

export default BlogPost;